import { useEffect, useRef, useState } from "react";
import {
  TransportSedel,
  TransportSedelStatus,
} from "types/transportSedel.type";

/**
 * Hook that detects if a TransportSedel's status changes to LoadedOnTruck
 * and provides animation state for visual feedback.
 * Only activates animation if the status *changes* to LoadedOnTruck, not if it's
 * already LoadedOnTruck initially.
 *
 * @param transportSedel The TransportSedel to monitor
 * @returns Object with isPulsing state to apply animation class
 */
export const useTransportSedelStatusAnimation = (
  transportSedel: TransportSedel,
): { isPulsing: boolean } => {
  const prevStatusRef = useRef(transportSedel.status);
  const [isPulsing, setIsPulsing] = useState(false);

  useEffect(() => {
    // Only detect the change if previous status was not LoadedOnTruck
    // and current status is LoadedOnTruck
    if (
      prevStatusRef.current !== TransportSedelStatus.LoadedOnTruck &&
      prevStatusRef.current !== undefined &&
      transportSedel.status === TransportSedelStatus.LoadedOnTruck
    ) {
      // Trigger the animation
      setIsPulsing(true);
      
      // Reset the animation after it completes
      const timeout = setTimeout(() => {
        setIsPulsing(false);
      }, 950); // Animation duration
      
      return () => clearTimeout(timeout);
    } else if (transportSedel.status !== TransportSedelStatus.LoadedOnTruck) {
      // Reset the pulsing state if status changes to something else
      setIsPulsing(false);
    }

    // Update the ref with current status for next render
    prevStatusRef.current = transportSedel.status;
  }, [transportSedel.status]);

  return { isPulsing };
};